// src/components/UpgradeModal.js
// ── Upgrade modal ─────────────────────────────────────────────────────────────
// Single reusable modal shown at every limit/gate point.
// Shown once per session per reason — dismissed state stored in sessionStorage.
//
// Usage:
//   <UpgradeModal
//     reason="event_limit"
//     currentTier="starter"
//     currentCount={5}
//     limit={5}
//     onClose={() => setShowUpgrade(false)}
//   />
//
// Reasons: "event_limit" | "user_limit" | "crm" | "meetings"

import { useEffect } from "react";
import { getUpgradeTier } from "../config/planLimits";

const FONT = "'Inter', -apple-system, BlinkMacSystemFont, sans-serif";
const CYAN = "#22D3EE";
const NAVY = "#0A1720";
const SURF = "#0F2230";
const BORD = "#1E3448";
const TEXT = "#FFFFFF";
const SEC  = "#B5B9BC";
const PURPLE = "#7C3AED";

const COPY = {
  event_limit: {
    icon:  "📅",
    title: (limit, tier) => `You've reached your ${limit}-event limit`,
    body:  (next) =>
      `Upgrade to ${next.label} to add up to ${next.events} per year — plus ${next.users} and ${next.tier === "professional" ? "CRM sync" : "unlimited everything"}.`,
    ctaPrimary:   (next) => next.tier === "enterprise" ? "Talk to sales" : `Upgrade to ${next.label}`,
    ctaSecondary: "Not now",
    ctaUrl:       (next) => next.tier === "enterprise" ? "/contact" : "/pricing",
    warning: (count, limit) => `You've used ${count} of ${limit} events this year.`,
  },
  user_limit: {
    icon:  "👥",
    title: (limit, tier) => `Your team is full`,
    body:  (next) =>
      `You're on ${next.tier === "professional" ? "Starter" : "Professional"} with a ${next.tier === "professional" ? "3" : "10"}-seat limit. Upgrade to ${next.label} to add ${next.users}.`,
    ctaPrimary:   (next) => next.tier === "enterprise" ? "Talk to sales" : `Upgrade to ${next.label}`,
    ctaSecondary: "Not now",
    ctaUrl:       (next) => next.tier === "enterprise" ? "/contact" : "/pricing",
    warning: (count, limit) => `You have ${limit - count} seat${limit - count === 1 ? "" : "s"} remaining.`,
  },
  crm: {
    icon:  "🔗",
    title: () => "CRM sync is a Professional feature",
    body:  () =>
      "Connect Salesforce or HubSpot to automatically sync leads and pull pipeline data back into ExIQ. Available on Professional and above.",
    ctaPrimary:   () => "Upgrade to Professional",
    ctaSecondary: "Learn more",
    ctaUrl:       () => "/pricing",
    warning:      () => null,
  },
  meetings: {
    icon:  "📆",
    title: () => "Meeting Scheduler is an Enterprise feature",
    body:  () =>
      "Schedule pre-event meetings, manage public booking pages, and track meeting-to-pipeline conversion across your entire event program.",
    ctaPrimary:   () => "Talk to sales",
    ctaSecondary: "Learn more",
    ctaUrl:       () => "/contact",
    warning:      () => null,
  },
};

export function UpgradeModal({
  reason,
  currentTier,
  currentCount = 0,
  limit = 0,
  onClose,
}) {
  const next = getUpgradeTier(currentTier);
  const copy = COPY[reason];
  if (!copy || !next) return null;

  const isEnterprise = next.tier === "enterprise";
  const accentColor  = isEnterprise ? PURPLE : CYAN;

  // Trap scroll behind modal
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  function handlePrimary() {
    const url = copy.ctaUrl(next);
    if (url.startsWith("/")) {
      window.location.href = url;
    } else {
      window.open(url, "_blank");
    }
    onClose?.();
  }

  function handleSecondary() {
    // Store dismissal for this session so it doesn't re-appear immediately
    try { sessionStorage.setItem(`upgrade_dismissed_${reason}`, "1"); } catch {}
    onClose?.();
  }

  return (
    // Backdrop
    <div
      onClick={handleSecondary}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(6,16,24,0.85)",
        backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24,
        fontFamily: FONT,
      }}
    >
      {/* Card */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: SURF,
          border: `1px solid ${accentColor}33`,
          borderRadius: 14,
          padding: "36px 32px",
          maxWidth: 420,
          width: "100%",
          boxShadow: `0 24px 64px rgba(0,0,0,0.5), 0 0 0 1px ${accentColor}22`,
          position: "relative",
        }}
      >
        {/* Close button */}
        <button
          onClick={handleSecondary}
          style={{
            position: "absolute", top: 16, right: 16,
            background: "transparent", border: "none",
            color: SEC, fontSize: 18, cursor: "pointer",
            lineHeight: 1, padding: 4,
          }}
        >×</button>

        {/* Icon */}
        <div style={{ fontSize: 36, marginBottom: 16, textAlign: "center" }}>
          {copy.icon}
        </div>

        {/* Tier badge */}
        <div style={{ textAlign: "center", marginBottom: 14 }}>
          <span style={{
            display: "inline-block",
            background: accentColor + "18",
            color: accentColor,
            fontSize: 10, fontWeight: 700,
            padding: "3px 12px", borderRadius: 20,
            letterSpacing: "0.08em", textTransform: "uppercase",
          }}>
            {next.label} feature
          </span>
        </div>

        {/* Title */}
        <div style={{
          fontSize: 18, fontWeight: 700, color: TEXT,
          textAlign: "center", marginBottom: 10, lineHeight: 1.3,
        }}>
          {copy.title(limit, currentTier)}
        </div>

        {/* Body */}
        <div style={{
          fontSize: 13, color: SEC, textAlign: "center",
          marginBottom: 28, lineHeight: 1.6,
        }}>
          {copy.body(next)}
        </div>

        {/* What you get box */}
        <div style={{
          background: accentColor + "0C",
          border: `1px solid ${accentColor}22`,
          borderRadius: 10, padding: "14px 18px",
          marginBottom: 24,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: accentColor, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            {next.label} includes
          </div>
          {[
            `${next.events} per year`,
            `${next.users}`,
            next.tier === "professional" ? "CRM sync (Salesforce + HubSpot)" : "Meeting scheduler + booking page",
            next.tier === "professional" ? "Full AI suite" : "Dedicated CSM",
          ].map((item, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <div style={{ color: accentColor, fontSize: 12, flexShrink: 0 }}>✓</div>
              <div style={{ fontSize: 13, color: TEXT }}>{item}</div>
            </div>
          ))}
          <div style={{ fontSize: 12, color: accentColor, fontWeight: 700, marginTop: 10 }}>
            {next.price}
          </div>
        </div>

        {/* CTAs */}
        <button
          onClick={handlePrimary}
          style={{
            width: "100%",
            background: accentColor,
            color: isEnterprise ? "#FFFFFF" : NAVY,
            border: "none",
            borderRadius: 8, padding: "12px 0",
            fontSize: 14, fontWeight: 700, cursor: "pointer",
            marginBottom: 10, fontFamily: FONT,
          }}
        >
          {copy.ctaPrimary(next)}
        </button>

        <button
          onClick={handleSecondary}
          style={{
            width: "100%",
            background: "transparent",
            color: SEC,
            border: `1px solid ${BORD}`,
            borderRadius: 8, padding: "10px 0",
            fontSize: 13, fontWeight: 600, cursor: "pointer",
            fontFamily: FONT,
          }}
        >
          {copy.ctaSecondary}
        </button>
      </div>
    </div>
  );
}

/**
 * Inline limit warning bar — shown at 80% usage.
 * Not a modal — renders inline wherever it's placed.
 */
export function LimitWarningBar({ resource, currentCount, limit, currentTier, onUpgradeClick }) {
  const next = getUpgradeTier(currentTier);
  if (!next) return null;

  const pct = limit > 0 ? currentCount / limit : 0;
  if (pct < 0.8) return null; // only show at 80%+

  const atLimit   = currentCount >= limit;
  const remaining = Math.max(0, limit - currentCount);

  const label = atLimit
    ? resource === "events"
      ? `Event limit reached — you're on ${currentTier} (${limit} events/year)`
      : `Seat limit reached — you're on ${currentTier} (${limit} users)`
    : resource === "events"
      ? `${remaining} event${remaining === 1 ? "" : "s"} remaining on your ${currentTier} plan`
      : `${remaining} seat${remaining === 1 ? "" : "s"} remaining on your ${currentTier} plan`;

  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      background: "#22D3EE0C", border: "1px solid #22D3EE33",
      borderRadius: 8, padding: "10px 16px",
      fontFamily: FONT, marginBottom: 12,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ fontSize: 14 }}>⚠</div>
        <span style={{ fontSize: 13, color: "#FFFFFF", fontWeight: 600 }}>{label}</span>
      </div>
      <button
        onClick={onUpgradeClick}
        style={{
          background: CYAN, color: NAVY,
          border: "none", borderRadius: 6,
          padding: "5px 12px", fontSize: 12,
          fontWeight: 700, cursor: "pointer", fontFamily: FONT,
          flexShrink: 0, marginLeft: 12,
        }}
      >
        Upgrade
      </button>
    </div>
  );
}
